import { ModalTypeFail, ModalTypeLoading, ModalTypeSuccess, PaymentTypeOptions } from "utils/Constant";
import { useModal } from "../../context/ModalContext";
import { useEffect, useMemo, useState } from "react";
import PlusIcon from "assets/icon/PlusIcon";
import { CheckResponse, GetPriceAfterTax, money, removeNonNumeric } from "utils/helpers";
import TrashIcon from "assets/icon/TrashIcon";
import NoImage from "assets/images/no-image.png";
import { PagesTitle } from "components/Cards";
import { useCallApi } from "api/apiHooks";

export default function SalesOrder(){
	const { openModal, closeModal } = useModal()
	const { fetchCategoryList, fetchSystem, fetchOrderSalesStockList, fetchCreateOrderSales } = useCallApi()
	
	const [categoryOptions, setCategoryOptions] = useState([])
	const [categoryId, setCategoryId] = useState('')
	const [cart, setCart] = useState([])
	const [searchParam, setSearchParam] = useState('')
	const [tax, setTax] = useState('0')
	const [discount, setDiscount] = useState('0')
	const [total, setTotal] = useState('')
	const [grandTotal, setGrandTotal] = useState('')
	const [salesStockList, setSalesStockList] = useState([])
	const [showModal, setShowModal] = useState(false)
	const [paymentType, setPaymentType] = useState('1')

	const filteredList = useMemo(() => {
		if (!Array.isArray(salesStockList)) return [];

		return salesStockList.filter((item) => {
		const matchCategory =
			!categoryId || item.categoryId === Number(categoryId);

		const matchSearch =
			!searchParam ||
			item.productName?.toLowerCase().includes(searchParam.toLowerCase()) || // match productName
		item.brandName?.toLowerCase().includes(searchParam.toLowerCase());    // match brandName

		return matchCategory && matchSearch;
		});
	}, [salesStockList, categoryId, searchParam]);

  // Button Event
  const addItemBtnClick = (data) => {
    setCart((prevCart) => {
      // find index in cart
      const existingIndex = prevCart.findIndex(
        (item) => 
          item.productId === data.productId &&
          item.size === data.size
      );

      // reduce stock in main list first
      setSalesStockList((prevList) =>
        prevList.map((item) => {
          if (
            item.productId === data.productId && 
            item.size === data.size && 
            item.stock > 0
          ) {
            // reduce stock only if it's > 0
            const newStock = item.stock > 0 ? item.stock - 1 : 0;
            return { ...item, stock: newStock };
          }
          return item;
        })
      );

      // if item already in cart
      if (existingIndex !== -1) {
        return prevCart.map((item, index) =>
          index === existingIndex
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }

      // item not in cart → only add if stock > 0
      if (data.stock <= 0) return prevCart;
      
      return [...prevCart, { ...data, qty: 1 }];
    });
  };
  const handleRemoveItem = (index) => {
    setCart((prevCart) => {
      const itemToRemove = prevCart[index];

      // increase stock in salesStockList
      setSalesStockList((prevList) =>
        prevList.map((item) =>
          item.productId === itemToRemove.productId && 
          item.size === itemToRemove.size
            ? { ...item, stock: item.stock + 1 }
            : item
        )
      );

      // reduce qty in cart, remove if qty reaches 0
      return prevCart
        .map((item, i) => {
          if (i === index) {
            return { ...item, qty: item.qty - 1 };
          }
          return item;
        })
        .filter((item) => item.qty > 0);
    });
  };
  const handleEmptyCart = () => {
    setSalesStockList((prevList) => {
      // Add back the quantities from the cart
      return prevList.map((item) => {
        const cartItem = cart.find((c) => c.productId === item.productId);
        if (cartItem) {
          return { ...item, stock: item.stock + cartItem.qty };
        }
        return item;
      });
    });

    // Clear the cart
    setCart([]);
  };
  const submitBtnClick = () => {
    openModal(ModalTypeLoading)

    fetchCreateOrderSales(cart, paymentType, tax, total, discount, grandTotal).then((res) => {
      if (CheckResponse(res)){
        fetchOrderSalesStockList('', '').then((res) => {
          if (!CheckResponse(res)) {
            openModal(ModalTypeFail, { message: res.message })
          }

          setSalesStockList(res.data.listData)
          setSearchParam('')
          setCategoryId('')

          openModal(ModalTypeSuccess, {
              onSuccess: () => {
                  closeModal()
              }
          })
        })
        setCart([])
      } else {
        openModal(ModalTypeFail, { message: res.message })
      }
    })
  }

  useEffect(() => {
    let result = cart?.reduce((sum, item) => sum + item.finalPrice * item.qty,0)
    setTotal(money(result))
    setGrandTotal(money(Math.round(result - (result * parseFloat(discount === '' ? 0 : discount) / 100))))
  }, [cart, discount])

  useEffect(() => {
    openModal(ModalTypeLoading)

    fetchSystem('tax').then((res) => {
      if (CheckResponse(res)) {
        setTax(res.data.systemValue)

        fetchCategoryList('', '1', 1000, 0).then((res) => {
          if (!CheckResponse(res)) {
            openModal(ModalTypeFail, { message: res.message })
            return
          }

          let data = res?.data?.listData
          let tempData = [{ label: 'All', value: '' }]

          data.map((data, i) => tempData.push({ label: data.categoryName, value: String(data.categoryId)}))

          setCategoryOptions(tempData)
          setCategoryId(tempData[0].value)

          fetchOrderSalesStockList('', '').then((res) => {
            if (CheckResponse(res)) {
              setSalesStockList(res.data.listData)
              closeModal()
            } else {
              openModal(ModalTypeFail, { message: res.message })
            }
          })
        })
      } else {
        openModal(ModalTypeFail, { message: res.message })
      }
    })
  }, [])

  return (
    <div className="p-3 h-screen overflow-hidden">
      <div className="w-full h-full bg-white border rounded-xl sm:p-6 flex flex-col">
        <PagesTitle title={'Sales Order'} />

        {/* CONTENT */}
        <div className="flex flex-1 gap-4 min-h-0 mt-4">

          {/* LEFT — SCROLLABLE */}
          <div className="w-full flex flex-col min-h-0 overflow-y-auto">

            {/* SEARCH BAR */}
            <div>
              <input
                className="border rounded-full w-full px-5 py-2"
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value)}
                placeholder="Search"
              />
            </div>

            {/* CATEGORY */}
            <div className="border flex gap-2 mt-3 bg-white shadow-md p-2 rounded-xl w-full
              min-h-14 overflow-x-auto flex-nowrap">
              {categoryOptions?.map((data, i) => (
                <div
                  key={i}
                  onClick={() => setCategoryId(data.value)}
                  className={`border rounded-xl px-3 cursor-pointer flex items-center justify-center
                    ${categoryId === String(data.value)
                      ? 'bg-black text-white'
                      : 'bg-white text-black'}`}
                >
                  <span className={`${categoryId === String(data.value) ? 'font-normal' : 'font-light'} whitespace-nowrap`}>{data.label}</span>
                </div>
              ))}
            </div>

            {/* PRODUCTS — this will overflow */}
            <div className="bg-white mt-3 shadow-md grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredList?.map((data, i) => (
                <div className="flex flex-col justify-between border rounded-lg p-3" key={i}>
                  <div>
                    <div className="w-full h-48 bg-gray-100 rounded" >
                      {/* IMAGE PLACEHOLDER */}
                      <img src={data.imageUrl === '' ? NoImage : data.imageUrl} alt={data.productName} className="w-full h-full object-contain rounded" />
                    </div>

                    <div className="flex flex-col mt-2">
                      <span className="">{data.brandName} - { data.productName } { data.size !== '' ? 'Size ' + data.size : '' }</span>
                      <div className="flex w-full">
                        <div>
                          <span className="font-thin text-sm whitespace-nowrap">
                            Rp { money(data.finalPrice  ) }
                          </span>
                        </div>
                        
                        { data.discount > 0 && (
                          <div className="flex justify-between w-full">
                            <div>
                              <span className="font-thin text-xs text-red-500 ml-2 line-through">
                                Rp { money(GetPriceAfterTax(data.price, tax)) }
                              </span>
                            </div>

                            <div className="border px-1 rounded bg-red-100 text-red-500">
                              <span className="text-xs">- {data.discount}%</span>
                            </div>
                          </div>
                          
                        )}
                      </div>
                      
                      <span className="font-thin text-sm text-gray-400">
                        Stock : { data.stock }
                      </span>
                    </div>
                  </div>
                  

                  <div className="mt-2">
                    <button className={`flex gap-2 w-full ${data.stock <= 0 ? 'bg-gray-200' : 'bg-black'} text-white px-3 py-2 rounded-full items-center justify-center`}
                      onClick={(() => { addItemBtnClick(data) })}
                      disabled={data.stock <= 0}
                    >
                      <PlusIcon className="w-5 fill-white" />
                      <span className="text-sm">Add to cart</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — FIXED CART */}
          <div className="border bg-white rounded-xl shadow-md min-w-80 max-w-80 p-4 shrink-0 flex flex-col h-full justify-between">
            <div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-xl">Cart</span>
                {cart?.length > 0 && (
                  <button className="bg-black text-white px-2 py-1 rounded-lg flex items-center"
                    onClick={handleEmptyCart}
                  >
                    <span className="text-xs">Empty Cart</span>
                  </button>
                )} 
              </div>
            
              <div className="mt-4 flex flex-col gap-2 h-[calc(100vh-23rem)] overflow-y-auto">
                {cart?.map((data, i) => {
                  return(
                    <div className="text-sm" key={i}>
                      <div className="flex gap-3">
                        <button className="flex items-center" onClick={() => {handleRemoveItem(i)}}>
                          <TrashIcon className="fill-black w-4 h-4" />
                        </button>

                        <div className="flex flex-col w-full">
                          <div>
                            <span>{data.brandName} - {data.productName} {data.size !== '' ? 'Size ' + data.size : ''}</span>
                          </div>

                          <div className="flex justify-between">
                            <div className="flex flex-col">
                              <span>{data.qty}x Rp { money(data.finalPrice) }</span>
                            </div>
                            <div>
                              <span className="font-semibold">Rp {money(data.finalPrice * data.qty)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr className="mt-2" />
                    </div>  
                  )
                })}

                {cart?.length < 1 && (
                  <div className="flex justify-center">
                    <span colSpan={4} className="text-sm text-gray-400">Cart is empty</span>
                  </div>
                )} 
              </div>
            </div>
            
            <div className="text-sm">
              <hr />
              <div className="mt-3 flex justify-between">
                <span className="">Total</span>
                <span className="">Rp {total}</span>
              </div>
              <div className=" flex justify-between">
                <span className="">Discount</span>
                <div className="flex gap-2">
                  <input className="w-12 text-right border rounded-lg px-2" value={discount} onChange={(e) => { setDiscount(removeNonNumeric(e.target.value)) }} />
                  <span className="">%</span>
                </div>
                
              </div>
              <div className="mb-8 flex justify-between">
                <span className="font-semibold">Grand Total</span>
                <span className="font-semibold">Rp {grandTotal}</span>
              </div>
              <button className={`text-white w-full py-2 rounded-full ${cart.length > 0 ? 'bg-black' : 'bg-gray-300'}`}
                onClick={() => { setShowModal(true) }}
                disabled={cart.length > 0 ? false : true}
              >
                <span>Submit</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL AFTER SUBMIT */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          
          {/* BACKDROP */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowModal(false)}
          />

          {/* MODAL BOX */}
          <div className="relative overflow-auto bg-white rounded-xl shadow-lg lg:w-9/12 xl:w-8/12 2xl:w-6/12 p-6 max-h-[90vh] overflow-y-auto">
            
            <h2 className="text-xl font-semibold mb-3">
              Confirm Transaction
            </h2>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to submit this order?
            </p>

            <span className="text-sm text-gray-600 mb-4">
              Payment Method
            </span>
            <div className="flex gap-2 mb-4">
              {PaymentTypeOptions.map((data, i) => {
                return (
                  <button 
                    className={` 
                      px-3 py-1 border rounded-full text-sm
                      ${data.value === paymentType ? 'bg-black text-white' : ''}`
                    }
                    onClick={() => {setPaymentType(data.value)}}
                  >
                    {data.label}
                  </button>
                )
              })}
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-full border"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 rounded-full bg-black text-white"
                onClick={() => {
                  submitBtnClick();
                  setShowModal(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}